import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';

const WebSocketContext = createContext(null);

export function WebSocketProvider({ children }) {
  const { user } = useAuth();
  // NOTE: AuthContext does not expose the raw JWT; the app stores it in
  // localStorage under `token`. Read it directly so the WS handshake uses
  // the same token that HTTP requests use — a mismatch here previously
  // meant `token` was undefined and the WS never connected.
  const token = typeof window !== 'undefined' ? window.localStorage.getItem('token') : null;
  const [isConnected, setIsConnected] = useState(false);
  const [subscribedChannels, setSubscribedChannels] = useState([]);
  const [typingUsers, setTypingUsers] = useState({}); // channel_id -> [{user_id, user_name}]
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const messageHandlersRef = useRef(new Map());
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const baseReconnectDelay = 1000;

  // Get WebSocket URL
  const getWsUrl = useCallback(() => {
    if (!token) return null;
    
    const loc = window.location;
    const wsProtocol = loc.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsProtocol}//${loc.host}/api/ws/chat?token=${token}`;
  }, [token]);

  // Send message to WebSocket
  const send = useCallback((data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
      return true;
    }
    return false;
  }, []);

  // Handle incoming messages
  const handleMessage = useCallback((data) => {
    const { type } = data;
    
    switch (type) {
      case 'presence_snapshot':
        messageHandlersRef.current.forEach(handler => handler.onPresence?.(data));
        break;

      case 'connected':
        console.log('[WS] Connected as:', data.user_name);
        break;
        
      case 'subscribed':
        setSubscribedChannels(data.channels || []);
        console.log('[WS] Subscribed to', data.channels?.length, 'channels');
        break;
        
      case 'new_message':
        // Notify all registered handlers for new messages
        messageHandlersRef.current.forEach((handler) => {
          if (handler.onMessage) {
            handler.onMessage(data.channel_id, data.message);
          }
        });
        break;
        
      case 'message_reaction':
        messageHandlersRef.current.forEach((handler) => {
          if (handler.onReaction) {
            handler.onReaction(data.channel_id, data.message_id, data.reactions);
          }
        });
        break;
        
      case 'message_deleted':
        messageHandlersRef.current.forEach((handler) => {
          if (handler.onDelete) {
            handler.onDelete(data.channel_id, data.message_id);
          }
        });
        break;

      case 'read_receipt':
        // Broadcast from backend when another user marked a channel as read.
        // Backend payload: { type, channel_id, user_id, user_name, read_at? }.
        // Prefer server-generated `read_at`; fall back to local time only
        // for backwards compatibility with older backends that omit it.
        messageHandlersRef.current.forEach((handler) => {
          if (handler.onReadReceipt) {
            handler.onReadReceipt(
              data.channel_id,
              data.user_id,
              data.user_name,
              data.read_at || new Date().toISOString()
            );
          }
        });
        break;
        
      case 'typing':
        setTypingUsers(prev => {
          const channelTyping = prev[data.channel_id] || [];
          if (data.is_typing) {
            // Add user if not already typing
            if (!channelTyping.find(u => u.user_id === data.user_id)) {
              return {
                ...prev,
                [data.channel_id]: [...channelTyping, { user_id: data.user_id, user_name: data.user_name }]
              };
            }
          } else {
            // Remove user from typing
            return {
              ...prev,
              [data.channel_id]: channelTyping.filter(u => u.user_id !== data.user_id)
            };
          }
          return prev;
        });
        
        // Auto-clear typing after 5 seconds (in case user disconnects)
        setTimeout(() => {
          setTypingUsers(prev => {
            const channelTyping = prev[data.channel_id] || [];
            return {
              ...prev,
              [data.channel_id]: channelTyping.filter(u => u.user_id !== data.user_id)
            };
          });
        }, 5000);
        break;
        
      case 'ping':
        send({ type: 'pong' });
        break;
        
      case 'pong':
        break;
        
      default:
        console.log('[WS] Unknown message type:', type);
    }
  }, [send]);

  // Connect to WebSocket
  const connect = useCallback(() => {
    const wsUrl = getWsUrl();
    if (!wsUrl || (wsRef.current && wsRef.current.readyState < WebSocket.CLOSING)) return;

    console.log('[WS] Connecting...');
    
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (wsRef.current !== ws) { ws.close(1000, 'Superseded'); return; }
        console.log('[WS] Connected');
        setIsConnected(true);
        ws.send(JSON.stringify({ type: 'presence', visible: document.visibilityState === 'visible' }));
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        if (wsRef.current !== ws) return;
        try {
          const data = JSON.parse(event.data);
          handleMessage(data);
        } catch (e) {
          console.error('[WS] Parse error:', e);
        }
      };

      ws.onclose = (event) => {
        if (wsRef.current !== ws) return;
        console.log('[WS] Disconnected:', event.code);
        setIsConnected(false);
        wsRef.current = null;
        
        // Auto-reconnect if not intentionally closed
        if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts && token) {
          const delay = baseReconnectDelay * Math.pow(2, reconnectAttemptsRef.current);
          reconnectAttemptsRef.current++;
          console.log(`[WS] Reconnecting in ${delay}ms...`);
          reconnectTimeoutRef.current = setTimeout(connect, delay);
        }
      };

      ws.onerror = (error) => {
        console.error('[WS] Error:', error);
      };
    } catch (e) {
      console.error('[WS] Connection error:', e);
    }
  }, [getWsUrl, handleMessage, token]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnect');
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // Set active channel (for "in chat" detection)
  const setActiveChannel = useCallback((channelId) => {
    send({ type: 'active_channel', channel_id: channelId });
  }, [send]);

  // Clear active channel
  const clearActiveChannel = useCallback(() => {
    send({ type: 'active_channel', channel_id: null });
  }, [send]);

  // Send typing indicator
  const sendTyping = useCallback((channelId, isTyping) => {
    send({ type: 'typing', channel_id: channelId, is_typing: isTyping });
  }, [send]);

  // Subscribe to channel
  const subscribeToChannel = useCallback((channelId) => {
    send({ type: 'subscribe', channel_id: channelId });
  }, [send]);

  // Unsubscribe from channel
  const unsubscribeFromChannel = useCallback((channelId) => {
    send({ type: 'unsubscribe', channel_id: channelId });
  }, [send]);

  // Register message handler (returns unregister function)
  const registerHandler = useCallback((id, handlers) => {
    messageHandlersRef.current.set(id, handlers);
    return () => {
      messageHandlersRef.current.delete(id);
    };
  }, []);

  // Get typing users for a channel
  const getTypingUsers = useCallback((channelId) => {
    return typingUsers[channelId] || [];
  }, [typingUsers]);

  // Auto-connect when user is logged in
  useEffect(() => {
    if (user && token) {
      // Small delay to ensure auth is fully set up
      const timer = setTimeout(connect, 500);
      return () => { clearTimeout(timer); disconnect(); };
    } else {
      disconnect();
    }
    
    return () => {
      disconnect();
    };
  }, [user, token, connect, disconnect]);

  // UI presence expires server-side if a suspended phone cannot send "hidden".
  useEffect(() => {
    if (!isConnected) return undefined;
    const publish = () => send({ type: 'presence', visible: document.visibilityState === 'visible' });
    const hide = () => send({ type: 'presence', visible: false });
    publish();
    const timer = setInterval(publish, 20000);
    document.addEventListener('visibilitychange', publish);
    window.addEventListener('pagehide', hide);
    window.addEventListener('pageshow', publish);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', publish);
      window.removeEventListener('pagehide', hide);
      window.removeEventListener('pageshow', publish);
    };
  }, [isConnected, send]);

  const requestPresence = useCallback(channelId => {
    send({ type: 'presence', visible: document.visibilityState === 'visible' });
    return send({ type: 'presence_query', channel_id: channelId });
  }, [send]);

  // Ping to keep connection alive
  useEffect(() => {
    if (!isConnected) return;
    
    const pingInterval = setInterval(() => {
      send({ type: 'ping' });
    }, 30000);
    
    return () => clearInterval(pingInterval);
  }, [isConnected, send]);

  const value = {
    isConnected,
    requestPresence,
    subscribedChannels,
    typingUsers,
    setActiveChannel,
    clearActiveChannel,
    sendTyping,
    subscribeToChannel,
    unsubscribeFromChannel,
    registerHandler,
    getTypingUsers,
    reconnect: connect
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocketContext() {
  const context = useContext(WebSocketContext);
  if (!context) {
    // Return a dummy context if not wrapped in provider
    return {
      isConnected: false,
      requestPresence: () => false,
      subscribedChannels: [],
      typingUsers: {},
      setActiveChannel: () => {},
      clearActiveChannel: () => {},
      sendTyping: () => {},
      subscribeToChannel: () => {},
      unsubscribeFromChannel: () => {},
      registerHandler: () => () => {},
      getTypingUsers: () => [],
      reconnect: () => {}
    };
  }
  return context;
}

export default WebSocketContext;
